import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  USER_OPERATIONS,
  RESOURCE_TYPE,
  API_RESOURCE_URLS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
} from "../../constants";
import defaultStyles from "./MasterShift.module.css";
import "../../cssModules/switch.css";
import { BackButton } from "..";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import MasterShiftToolbar from "../MasterShiftToolbar/MasterShiftToolbar";
import { getMasterShiftPerPlant } from "../../apis/calls";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useAjaxCallbackWithIntervals } from "../../hooks";
import { useHistory } from "react-router-dom";
import moment from "moment";
import ShiftMasterForm from "../ShiftMasterForm/ShiftMasterForm";
import { API } from "../../apis/api";

const MasterShift = ({
  setHasActiveEdits,
  hasActiveEdits,
  handleCountChange,
}) => {
  const ROW_HEIGHT = 38;
  const RESOURCE_OPERATION = USER_OPERATIONS.ADD_MASTER_SHIFT;
  const { showError, showInternalError, showPopup } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const history = useHistory();

  const [styles, setStyles] = useState(defaultStyles);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const [asnCount, setAsnCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const setCount = (count) => {
    setAsnCount(count);
  };
  const fixedColumns = [
    {
      width: 140,
      title: "Shift Name",
      field: "validShiftName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Start Time",
      field: "startTime",
      render: ({startTime}) => {
        const utcTime = moment.utc('2022-10-31T'+startTime);
        const validTimeStamp = utcTime.utcOffset('+05:30').format('hh:mm A');
        return validTimeStamp;
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "End Time",
      field: "endTime",
      render: ({endTime}) => {
        const utcTime = moment.utc('2022-10-31T'+endTime);
        const validTimeStamp = utcTime.utcOffset('+05:30').format('hh:mm A');
        return validTimeStamp;
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 160,
      title: "Active from",
      field: "activeFromDate",
      render: ({ activeFromDate }) => {
        const validTimeStamp = moment.utc(activeFromDate);
        return moment(validTimeStamp).format("DD/MM/yyyy");
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Active to",
      field: "activeToDate",
      render: ({ activeToDate }) => {
        const validTimeStamp = moment.utc(activeToDate);
        return moment(validTimeStamp).format("DD/MM/yyyy");
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 160,
      title: "Updated at",
      field: "modifiedAt",
      render: ({ modifiedAt }) => {
        const validTimeStamp = moment.utc(modifiedAt);
        return moment(validTimeStamp).format("hh:mma DD/MM/yyyy");
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Updated by",
      field: "modifiedBy",
      enableSearch: true,
      enableFilter: true,
    },
  ].filter((c) => c);

  const redirectToApplicationLandingPage = () => {
    history.push("/");
  };

  const getMasterShiftData = async () => {
    if (plant.value) {
      setRowsData({ loading: true, ...rowsData });
      try {
        let response = await getMasterShiftPerPlant(plant.value);
        response.data = sortShifts(response.data);
        setRowsData({
          loading: false,
          data: response.data,
          filteredData: response.data,
        });
      } catch (err) {
        console.log(err);
        showInternalError({ error: err });
      }
    }
  };

  const sortShifts = (shifts) => {
    shifts.sort((shift1, shift2) => {
      if (
        moment
          .utc(shift2.activeToDate)
          .isBefore(moment.utc(shift1.activeToDate))
      ) {
        return -1;
      }
      if (
        moment.utc(shift2.activeToDate).isAfter(moment.utc(shift1.activeToDate))
      ) {
        return 1;
      }
      if (
        shift1.validShiftName.toUpperCase() >
        shift2.validShiftName.toUpperCase()
      ) {
        return 1;
      }
      if (
        shift1.validShiftName.toUpperCase() <
        shift2.validShiftName.toUpperCase()
      ) {
        return -1;
      }
      return 0;
    });

    return shifts;
  };

  const lastRefreshedShifts = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasActiveEdits,
    getMasterShiftData,
    plant.value
  );

  const openFormPopup = () => {
    setShowForm(true);
  };

  const closeFormPopup = () => {
    setShowForm(false);
  };

  const addMasterShift = async(formData) => {
   
    if(plant.value){
      try{
        const response = await API.post(API_RESOURCE_URLS.addMasterShift(plant.value), formData);
        showPopup({
          type: MESSAGE_TYPE.SUCCESS,
          contextText: ' ',
          info: DISPLAY_MESSAGES.ADDED_MASTER_SHIFT,
        });
        closeFormPopup();
        getMasterShiftData();
      }catch(error){
        showError({
          contextText: DISPLAY_MESSAGES.ADD_MASTER_SHIFT_ERR_CONTEXT,
          info: DISPLAY_MESSAGES.ADD_MASTER_SHIFT_ERR_Info(formData.shiftName),
        });
      }
    }
  };

  useEffect(() => {
    handleCountChange(asnCount);
  }, [asnCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          action="Shift Configurator"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <MasterShiftToolbar plantName={plant.label} />
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={fixedColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            primaryAction={{
              name: "SET-UP",
              actionFn: () => {
                openFormPopup();
              },
              authOperation: RESOURCE_OPERATION,
            }}
          />
        </div>
      </div>

      {showForm && (
        <ShiftMasterForm
          closeFormPopup={closeFormPopup}
          addMasterShift={addMasterShift}
        />
      )}
    </>
  );
};

MasterShift.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  MasterShift,
  false,
  RESOURCE_TYPE.MASTER_SHIFT
);
